import React, { useCallback, useEffect, useState } from 'react';

import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  ContractCommentResult,
  createContractComment,
  listContractComment,
} from 'stores/ContractCommentStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

/** 名前から安定した色を生成（アバター用） */
const avatarColor = (name: string): string => {
  const palette = ['#1565c0', '#6a1b9a', '#2e7d32', '#00838f', '#ed6c02', '#c62828', '#5e35b1'];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
};

type CommentSectionProps = {
  contractId: string;
  contractRequestId?: string;
};

const CommentSection: React.FC<CommentSectionProps> = ({ contractId, contractRequestId }) => {
  const [comments, setComments] = useState<ContractCommentResult[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTarget, setReplyTarget] = useState<{
    commentId: string;
    userName: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(() => {
    listContractComment({ contractId })
      .then(setComments)
      .catch(() => errorDefault('コメントの取得に失敗しました'));
  }, [contractId]);

  useEffect(() => {
    if (contractId) {
      fetchComments();
    }
  }, [contractId, fetchComments]);

  const handleSubmit = () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    createContractComment({
      contractId,
      contractRequestId,
      comment: newComment.trim(),
      parentCommentId: replyTarget?.commentId,
    })
      .then(() => {
        setNewComment('');
        setReplyTarget(null);
        fetchComments();
      })
      .catch((e: Error) => errorDefault('コメントの登録に失敗しました', e.message))
      .finally(() => setSubmitting(false));
  };

  const handleReply = (commentId: string, userName: string) => {
    setReplyTarget({ commentId, userName });
  };

  const handleCancelReply = () => {
    setReplyTarget(null);
  };

  return (
    <Stack spacing={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* コメント一覧 */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            コメントはありません
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {comments.map((comment) => (
              <CommentThread
                key={comment.contractCommentId}
                comment={comment}
                onReply={handleReply}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* 入力欄 */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
        {replyTarget && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="primary.main">
              @{replyTarget.userName} への返信
            </Typography>
            <Button size="small" onClick={handleCancelReply} sx={{ ml: 1, textTransform: 'none' }}>
              キャンセル
            </Button>
          </Box>
        )}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={4}
            placeholder="コメントを入力..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleSubmit();
              }
            }}
            slotProps={{ htmlInput: { maxLength: 10000 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <IconButton
            color="primary"
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
            sx={{ flexShrink: 0 }}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Box>
    </Stack>
  );
};

// --- CommentThread ---
type CommentThreadProps = {
  comment: ContractCommentResult;
  onReply: (commentId: string, userName: string) => void;
};

const CommentThread: React.FC<CommentThreadProps> = ({ comment, onReply }) => {
  return (
    <Box>
      {/* 親コメント */}
      <CommentItem
        commentId={comment.contractCommentId}
        userName={comment.createdByName}
        createdAt={comment.createdAt}
        text={comment.comment}
        onReply={onReply}
      />
      {/* 子コメント（左に返信ラインを引いてインデント） */}
      {comment.children.length > 0 && (
        <Stack
          spacing={0.5}
          sx={{ ml: 2.5, mt: 0.5, borderLeft: 2, borderColor: 'divider', pl: 1.5 }}
        >
          {comment.children.map((child) => (
            <CommentItem
              key={child.contractCommentId}
              commentId={child.parentCommentId}
              userName={child.createdByName}
              createdAt={child.createdAt}
              text={child.comment}
              onReply={onReply}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

// --- CommentItem ---
type CommentItemProps = {
  commentId: string;
  userName: string;
  createdAt: string;
  text: string;
  onReply: (commentId: string, userName: string) => void;
};

const CommentItem: React.FC<CommentItemProps> = ({
  commentId,
  userName,
  createdAt,
  text,
  onReply,
}) => {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        py: 1,
        px: 1,
        borderRadius: 2,
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .comment-reply': { opacity: 1 },
      }}
    >
      <Avatar
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: avatarColor(userName),
        }}
      >
        {userName.charAt(0)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {userName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatDateTime(createdAt)}
          </Typography>
        </Stack>
        {/* 吹き出し風の本文 */}
        <Box
          sx={{
            mt: 0.5,
            px: 1.5,
            py: 1,
            bgcolor: 'action.hover',
            borderRadius: 2,
            borderTopLeftRadius: 4,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text}
          </Typography>
        </Box>
        <Button
          className="comment-reply"
          size="small"
          startIcon={<ReplyIcon sx={{ fontSize: 15 }} />}
          onClick={() => onReply(commentId, userName)}
          sx={{
            mt: 0.25,
            px: 0.75,
            minWidth: 0,
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.72rem',
            opacity: { xs: 1, md: 0 },
            transition: 'opacity 0.15s',
            '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
          }}
        >
          返信
        </Button>
      </Box>
    </Stack>
  );
};

export default CommentSection;
